import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Link from 'components/shared/Link'
import SeparatedList from 'components/shared/SeparatedList'
import InlineTextList from 'components/shared/InlineTextList'
import TicketRow from './TicketRow'
import { useResolutions } from 'store/resolutions'
import DateTitle from 'components/shared/DateTitle'
import { FormattedMessage } from 'react-intl'
import { useApp } from 'store/steps'
import Loader from 'components/shared/Loader'
import { sortDates } from 'helpers/helpers'
import { Color } from 'styles/types'
import { ReservationRowProps } from 'components/shared/Reservation/types'
import useTicketTranslations from 'utils/hooks/useTicketTranslations'
import { TicketReservation } from 'store/selfcare-slice/types'

interface TicketsRowProps extends ReservationRowProps {
    tickets: TicketReservation[]
}

const TicketsRow = ({
    mode,
    handleEdit,
    tickets,
    isDownloadingAttachementsDisabled = false,
}: TicketsRowProps) => {
    const { isTablet } = useResolutions()
    const { stepId, language } = useApp()
    const { getTicketPersonByKey, getTicketZoneByKey, getTicketTypeByKey } =
        useTicketTranslations()

    const isSummary = stepId === 'summary'

    const ticketDays = sortDates(
        Array.from(
            new Set(
                tickets.map(
                    (ticket) =>
                        ticket[`${ticket.changes ? 'changes' : 'original'}`]
                            .detail.day
                )
            )
        )
    )

    return (
        <Box isFullWidth>
            <Box
                direction="row"
                align="center"
                justify="space-between"
                isFullWidth
                mb={[16, 24]}
            >
                <Text variant="heading3" color="secondary_700">
                    <FormattedMessage
                        id="selfcare.tickets.section_caption"
                        defaultMessage="Bilety"
                    />
                </Text>
                {mode === 'reservation' && !isDownloadingAttachementsDisabled && (
                    <Link
                        variant="primary"
                        textVariant="bodySmallStrong"
                        icon={null}
                        path={`${process.env.REACT_APP_API_URL}api/${language}/selfcare/attachment/ticket`}
                        data-testid="selfcare-button-download-ticket"
                    >
                        <FormattedMessage
                            id="selfcare.download_tickets"
                            defaultMessage="Pobierz bilety"
                        />
                    </Link>
                )}
                {mode === 'summary' && handleEdit && (
                    <Link
                        variant="gray"
                        icon={null}
                        onClick={() => handleEdit('manage-tickets')}
                        data-testid="selfcare-summary-button-edit-tickets"
                    >
                        <FormattedMessage
                            id="selfcare.edit"
                            defaultMessage="Edytuj"
                        />
                    </Link>
                )}
            </Box>
            <Box isFullWidth gap={32}>
                {ticketDays.map((day, index) => {
                    const ticketsPerDay = tickets.filter(
                        (ticket) =>
                            ticket[`${ticket.changes ? 'changes' : 'original'}`]
                                .detail.day === day
                    )

                    return (
                        <Box isFullWidth key={`ticket-${day}`}>
                            <DateTitle index={index + 1} date={day} />
                            <SeparatedList
                                direction="column"
                                gap={[16, 24]}
                                isFullWidth
                                borderWidth={2}
                                borderColor="grayscale_300"
                                hasLastItemBorder
                                mt={[16, 24]}
                            >
                                {ticketsPerDay.map((ticket, index) => {
                                    const { original, changeType, changes } =
                                        ticket

                                    const isReturned =
                                        isSummary && changeType === 'return'

                                    const isChanged =
                                        isSummary && changeType === 'change'

                                    let textColor: Color = 'grayscale_900'

                                    if (isReturned) {
                                        textColor = 'grayscale_700'
                                    }

                                    if (isChanged) {
                                        textColor = 'primary_500'
                                    }

                                    const dataToDisplay =
                                        isSummary && changes
                                            ? changes
                                            : original

                                    const { detail, price } =
                                        dataToDisplay || {}
                                    const { person, ticketTime, zone } =
                                        detail || {}

                                    const personTitle =
                                        getTicketPersonByKey(person)
                                    const zoneTitle = getTicketZoneByKey(zone)
                                    const ticketTitle =
                                        getTicketTypeByKey(ticketTime)

                                    return (
                                        <TicketRow
                                            price={price || 0}
                                            key={`ticket-row-${index}`}
                                            isReturned={isReturned}
                                        >
                                            {!personTitle ||
                                            !zoneTitle ||
                                            !ticketTitle ? (
                                                <Loader />
                                            ) : (
                                                <>
                                                    {isTablet ? (
                                                        <Box direction="column">
                                                            <Text
                                                                variant="bodySmall"
                                                                color={
                                                                    textColor
                                                                }
                                                                mb={8}
                                                            >
                                                                {personTitle}
                                                            </Text>
                                                            <InlineTextList
                                                                variant="bodySmall"
                                                                color={
                                                                    textColor
                                                                }
                                                                items={[
                                                                    zoneTitle,
                                                                    ticketTitle,
                                                                ]}
                                                                hasNoWrapItems
                                                                gap={16}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <InlineTextList
                                                            variant="bodySmall"
                                                            color={textColor}
                                                            items={[
                                                                personTitle,
                                                                zoneTitle,
                                                                ticketTitle,
                                                            ]}
                                                            hasNoWrapItems
                                                            gap={16}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </TicketRow>
                                    )
                                })}
                            </SeparatedList>
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}

export default TicketsRow
